import { RefObject, useCallback, useEffect, useRef } from 'react';
import { BlockerFunction, useBlocker } from 'react-router';

import CancelButton from 'components/buttons/CancelButton';
import SaveButton from 'components/buttons/SaveButton';
import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import type { DraftLeave } from 'components/create-dialog/useCreateDraft';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { ModalHeader } from 'components/modals/ModalHeader';
import type { ModalClassNames } from 'components/modals/ModalProvider';
import { useModal } from 'components/modals/useModal';
import { Control, FieldValues, useFormState } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { t } from 'utils/getTranslations';

type Props<T extends FieldValues> = {
    control: Control<T>;
    message?: string;
    titleTextId?: string;
    confirmTextId?: string;
    cancelTextId?: string;
    bypassRef?: RefObject<boolean>;
    /** A create form's way to keep the changes as a draft, offered beside leaving. */
    draft?: DraftLeave | null;
};

/**
 * Classic draws the leave question a third of the way down, over the page dimmed darker than a plain modal's, and
 * unblurred. The new design keeps its centred dialog and its own backdrop.
 */
const DRAFT_LEAVE_PLACEMENT: ModalClassNames = {
    panel: 'top-1/3 translate-y-0 new:top-1/2 new:-translate-y-1/2',
    overlay: 'bg-black/45 supports-backdrop-filter:backdrop-blur-none',
};

type DraftLeaveModalProps = { draft: DraftLeave; onBack: () => void; onLeave: () => void; onSaved: () => void };

/** The leave question of a create form that can be saved as a draft: back, leave without saving, or save it so. */
export function DraftLeaveModal({ draft, onBack, onLeave, onSaved }: DraftLeaveModalProps) {
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const save = async () => {
        // Refused, the form shows why once the question is gone.
        if (await draft.save()) {
            onSaved();
        } else {
            onBack();
        }
    };

    return (
        <>
            <ModalHeader title={t('confirm-navigation-modal.title')} onCancel={onBack} />
            <p className="m-0 p-6 max-sm:px-4">
                <FormattedMessage
                    id="confirm-navigation-modal.draft-message"
                    values={{ record: t(`create-dialog.record.${draft.record}`) }}
                />
            </p>
            <div className="border-divider bg-grey-0 flex min-h-14 flex-wrap items-center justify-end gap-2 rounded-b-[12px] border-t px-6 py-2">
                <CancelButton onClick={onBack} disabled={buttonsDisabled}>
                    <FormattedMessage id="confirm-navigation-modal.no" />
                </CancelButton>
                <CancelButton onClick={onLeave} disabled={buttonsDisabled}>
                    <FormattedMessage id="confirm-navigation-modal.yes" />
                </CancelButton>
                <SaveButton onClick={save} disabled={buttonsDisabled}>
                    <FormattedMessage id="create-dialog.actions.save-draft" />
                </SaveButton>
            </div>
        </>
    );
}

/**
 * Asks before the route changes or the tab unloads while the form has unsaved changes. A create form that can be
 * saved as a draft offers that too. A form in the create dialog is on no route of its own, so there it guards
 * nothing: the page behind stays free to change, and the dialog asks its own question.
 */
const ConfirmNavigation = <T extends FieldValues>(props: Props<T>) => {
    const inCreateDialog = useInCreateDialog();

    return inCreateDialog ? null : <RouteConfirmNavigation {...props} />;
};

function RouteConfirmNavigation<T extends FieldValues>({
    control,
    titleTextId = 'confirm-navigation-modal.title',
    message = t('confirm-navigation-modal.default-message'),
    confirmTextId = 'confirm-navigation-modal.yes',
    cancelTextId = 'confirm-navigation-modal.no',
    bypassRef,
    draft,
}: Props<T>) {
    const { openModal, closeModal } = useModal();
    const { isDirty } = useFormState({ control });
    const draftLeaving = draft?.leaving;

    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) => {
            if (bypassRef?.current === true || draftLeaving?.current === true) {
                return false;
            }
            return isDirty && currentLocation.pathname !== nextLocation.pathname;
        },
        [isDirty, bypassRef, draftLeaving]
    );

    const blocker = useBlocker(shouldBlock);
    // Read as the question opens: the form hands a new one on every render.
    const draftRef = useRef(draft);
    useEffect(() => {
        draftRef.current = draft;
    });
    const isBlockedState = blocker.state === 'blocked';

    // Store the latest beforeunload handler logic in a ref so the window listener has
    // stable identity across renders (advanced-event-handler-refs).
    const handleRouteChangeRef = useRef<(event: BeforeUnloadEvent) => string>(() => message);
    handleRouteChangeRef.current = (event: BeforeUnloadEvent) => {
        if (isDirty) {
            event.preventDefault();
        }
        return message;
    };

    // Stable listener identity for add/removeEventListener. Created once.
    const stableRouteChangeListenerRef = useRef<((event: BeforeUnloadEvent) => string) | undefined>(undefined);
    if (!stableRouteChangeListenerRef.current) {
        stableRouteChangeListenerRef.current = (event: BeforeUnloadEvent) =>
            handleRouteChangeRef.current?.(event) ?? '';
    }

    useEffect(() => {
        const listener = stableRouteChangeListenerRef.current!;
        if (isBlockedState && !isDirty) {
            window.removeEventListener('beforeunload', listener);
            blocker.reset?.();
        } else {
            window.addEventListener('beforeunload', listener);
        }

        return () => {
            window.removeEventListener('beforeunload', listener);
        };
    }, [blocker, isDirty, isBlockedState]);

    const handleProceed = useCallback(() => {
        closeModal();
        blocker.proceed?.();
    }, [closeModal, blocker]);

    const handleCancel = useCallback(() => {
        closeModal();
        blocker.reset?.();
    }, [closeModal, blocker]);

    // TODO(react-doctor: no-effect-event-handler): driven by router blocker
    // state (async navigation interception) — framework pattern, not a direct event.
    useEffect(() => {
        if (isBlockedState) {
            const leave = draftRef.current;
            openModal(
                leave ? (
                    <DraftLeaveModal
                        draft={leave}
                        onBack={handleCancel}
                        onLeave={handleProceed}
                        onSaved={handleProceed}
                    />
                ) : (
                    <ConfirmationModal
                        content={message}
                        onCancel={handleCancel}
                        onConfirm={handleProceed}
                        confirmId={confirmTextId}
                        cancelId={cancelTextId}
                        titleId={titleTextId}
                    />
                ),
                handleCancel,
                undefined,
                undefined,
                leave ? DRAFT_LEAVE_PLACEMENT : undefined
            );
        }
    }, [isBlockedState, cancelTextId, confirmTextId, handleCancel, handleProceed, message, openModal, titleTextId]);

    return null;
}

export default ConfirmNavigation;
